import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {getDoc, setDoc} from '../../../firebase/firestore/firestore';
import {
    AVAILABLE_PRODUCT_COLUMNS,
    DEFAULT_VISIBLE_COLUMN_FIELDS,
} from '../constants/tableColumns';

const COLLECTION = 'products_table_columns';

// Columns that can never be hidden (e.g. the product title link).
const LOCKED_FIELDS = AVAILABLE_PRODUCT_COLUMNS.filter((c) => c.locked).map((c) => c.field);

/**
 * Per-user visible-column preferences for the products list, persisted to the backend
 * generic_documents store (collection `products_table_columns`, id = userId).
 *
 * Persistence is optimistic + fire-and-forget, mirroring useSavedFilters: local state is the
 * source of truth for the session; the next load reconciles with the server. Locked columns
 * (e.g. title) are always visible regardless of stored state.
 *
 * @param {string} userId
 * @returns {{ columns, visibleFields, toggleField, isVisible, resetColumns, loading }}
 */
export function useProductsColumns(userId) {
    const [visibleFields, setVisibleFields] = useState(DEFAULT_VISIBLE_COLUMN_FIELDS);
    const [loading, setLoading] = useState(true);

    const visibleRef = useRef(visibleFields);
    visibleRef.current = visibleFields;

    useEffect(() => {
        if (!userId) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        getDoc(COLLECTION, userId)
            .then((doc) => {
                if (cancelled) return;
                const stored = Array.isArray(doc?.fields) ? doc.fields : null;
                // Keep only fields that still exist in the current column catalog, then
                // force-include locked fields so title can't be lost from stale data.
                const known = new Set(AVAILABLE_PRODUCT_COLUMNS.map((c) => c.field));
                const next = stored
                    ? Array.from(new Set([...LOCKED_FIELDS, ...stored.filter((f) => known.has(f))]))
                    : DEFAULT_VISIBLE_COLUMN_FIELDS;
                setVisibleFields(next);
            })
            .catch(() => setVisibleFields(DEFAULT_VISIBLE_COLUMN_FIELDS))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [userId]);

    const persist = useCallback((next) => {
        setVisibleFields(next);
        if (userId) setDoc(COLLECTION, userId, {fields: next}).catch(() => {});
    }, [userId]);

    const toggleField = useCallback((field) => {
        if (LOCKED_FIELDS.includes(field)) return;
        const current = visibleRef.current;
        const next = current.includes(field)
            ? current.filter((f) => f !== field)
            : [...current, field];
        persist(next);
    }, [persist]);

    const resetColumns = useCallback(() => {
        persist(DEFAULT_VISIBLE_COLUMN_FIELDS);
    }, [persist]);

    const isVisible = useCallback((field) => visibleFields.includes(field), [visibleFields]);

    // Ordered visible columns, preserving the canonical catalog order.
    const columns = useMemo(
        () => AVAILABLE_PRODUCT_COLUMNS.filter((c) => visibleFields.includes(c.field)),
        [visibleFields],
    );

    return {columns, visibleFields, toggleField, isVisible, resetColumns, loading};
}
